/**
 * Recurring templates (ADR 0024): while the app runs (also hidden in the tray),
 * make the pages whose moment has come, and say so in the bell and a toast.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { runRecurringTemplates } from '@nexus/workspace';
import { useStore } from '../db/context';
import { useToasts } from '../notifications/toasts';
import { useSyncState } from '../sync/useSync';

const EVERY_MS = 60_000;

export function useRecurringTemplates() {
  const store = useStore();
  const { t, i18n } = useTranslation();
  // Signed in: only after the first sync, so a page made on another device counts.
  const ready = useSyncState((s) => s.authReady && (!s.session || !!s.status.lastSyncAt));

  useEffect(() => {
    if (!ready) return;
    let busy = false;
    const run = async () => {
      if (busy) return;
      busy = true;
      try {
        for (const page of await runRecurringTemplates(store, { locale: i18n.language })) {
          const n = await store.addNotification({
            key: `template:${page.id}`,
            kind: 'reminder',
            taskId: null,
            pageId: page.id,
            title: page.title,
            body: t('templates.made'),
            fireAt: Date.now(),
          });
          useToasts.getState().push(n);
        }
      } catch (e) {
        console.error('recurring templates', e);
      } finally {
        busy = false;
      }
    };
    void run();
    const timer = setInterval(() => void run(), EVERY_MS);
    return () => clearInterval(timer);
  }, [store, ready, t, i18n.language]);
}
